import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { superAdminAPI } from '../../services/api';
import { ExportMenu } from '../../components/SuperAdmin/ListTools';
import { formatExportDate } from '../../utils/exportData';
import {
  HiOutlineChartBar,
  HiOutlineRefresh,
  HiOutlineTrendingUp,
  HiOutlineTrendingDown,
} from 'react-icons/hi';
import { ReportGuide, periodLabel } from '../../components/SuperAdmin/ReportGuide';

const PERIODS = [7, 30, 90, 180];

const formatDay = (ts) => {
  const d = new Date(ts);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

const TicketTrendAnalysis = () => {
  const [days, setDays] = useState(30);

  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: ['ticket-trend-analysis', days],
    queryFn: () => superAdminAPI.getTicketTrendAnalysis({ days }),
    refetchInterval: 120000,
  });

  // Axios → res.data (server body) → body.data
  const body = data?.data?.data || {};
  const stats = body.stats || {};
  const buckets = Array.isArray(body.buckets) ? body.buckets : [];

  // Scale by the TALLEST stacked column (created+resolved+pending) so every
  // day's stack fits inside the fixed-height chart area without clipping
  const maxStack = Math.max(
    ...buckets.map((b) => (b.created || 0) + (b.resolved || 0) + (b.pending || 0)),
    1
  );
  const barHeight = (v) => `${(v / maxStack) * 140}px`;

  // Show date labels on every ~Nth bucket so they never overlap
  const labelStep = Math.max(1, Math.ceil(buckets.length / 10));

  const trendUp = (stats.createdTrendPct ?? 0) > 0;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex-1 basis-80">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <HiOutlineChartBar className="w-7 h-7 text-primary-500" />
            Ticket Trends
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Day-by-day view of tickets coming in versus tickets solved
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs font-medium text-gray-500">Show last</span>
          <div className="flex flex-wrap gap-1 bg-gray-100 rounded-xl p-1">
            {PERIODS.map((p) => (
              <button
                key={p}
                onClick={() => setDays(p)}
                className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  days === p ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {periodLabel(p)}
              </button>
            ))}
          </div>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-medium
                       hover:bg-primary-600 shadow-lg shadow-primary-500/25 disabled:opacity-50"
          >
            <HiOutlineRefresh className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <ReportGuide
        id="ticket-trends"
        summary="If more tickets come in than get solved, the backlog grows. Use this to spot busy days and rising trends."
        points={[
          ["Created", "New tickets raised that day."],
          ["Resolved", "Tickets solved that day."],
          ["Pending", "Tickets still open at the end of the day."],
          ["Creation trend", "Whether new tickets are rising or falling compared with the first half of the period."],
        ]}
      />

      {/* Error banner — otherwise a failed request renders as all zeros */}
      {isError && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center justify-between">
          <p className="text-sm text-red-700">Failed to load the trend analysis — please try again.</p>
          <button
            onClick={() => refetch()}
            className="ml-4 px-3 py-1.5 rounded-lg bg-red-100 text-red-700 text-sm font-medium hover:bg-red-200 flex-shrink-0"
          >
            Retry
          </button>
        </div>
      )}

      {/* Stat tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Tickets Created</p>
          <p className="text-3xl font-bold text-primary-500 mt-2">{stats.totalCreated ?? 0}</p>
          <p className="text-xs text-gray-400 mt-1">Last {body.windowDays ?? days} days</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Tickets Resolved</p>
          <p className="text-3xl font-bold text-green-500 mt-2">{stats.totalResolved ?? 0}</p>
          <p className="text-xs text-gray-400 mt-1">In the same window</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Currently Open</p>
          <p className="text-3xl font-bold text-amber-500 mt-2">{stats.currentlyOpen ?? 0}</p>
          <p className="text-xs text-gray-400 mt-1">Pending right now</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Avg / Day</p>
          <p className="text-2xl font-bold text-gray-900 mt-2">
            <span className="text-primary-500">+{stats.avgCreatedPerDay ?? 0}</span>
            <span className="text-gray-300 mx-1">/</span>
            <span className="text-green-500">-{stats.avgResolvedPerDay ?? 0}</span>
          </p>
          <p className="text-xs text-gray-400 mt-1">Created / resolved per day</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Creation Trend</p>
          <p className={`text-3xl font-bold mt-2 flex items-center gap-1.5 ${
            trendUp ? 'text-red-500' : 'text-green-500'
          }`}>
            {trendUp ? <HiOutlineTrendingUp className="w-6 h-6" /> : <HiOutlineTrendingDown className="w-6 h-6" />}
            {Math.abs(stats.createdTrendPct ?? 0)}%
          </p>
          <p className="text-xs text-gray-400 mt-1">{trendUp ? 'Rising' : 'Falling'} vs first half of window</p>
        </div>
      </div>

      {/* Trend chart */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-gray-700">Daily Trend</h2>
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm bg-blue-500 inline-block" />
              Created
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm bg-green-500 inline-block" />
              Resolved
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm bg-amber-400 inline-block" />
              Pending
            </span>
          </div>
        </div>

        {isLoading ? (
          <div className="h-48 flex items-center justify-center text-gray-400 text-sm">
            Loading trend analysis…
          </div>
        ) : buckets.length === 0 ? (
          <div className="h-48 flex items-center justify-center text-gray-400 text-sm">
            No trend data in this period
          </div>
        ) : (
          <div>
            <div className="flex items-end gap-[2px] h-48">
              {buckets.map((b) => (
                <div
                  key={b.date}
                  className="flex-1 flex flex-col justify-end items-center gap-[1px] min-w-[6px] group relative"
                  title={`${formatDay(b.date)} — Created: ${b.created}, Resolved: ${b.resolved}, Pending: ${b.pending}`}
                >
                  <div
                    className="w-full bg-blue-500 rounded-t-sm group-hover:bg-blue-600 transition-colors"
                    style={{ height: barHeight(b.created), minHeight: b.created > 0 ? '2px' : 0 }}
                  />
                  <div
                    className="w-full bg-green-500 group-hover:bg-green-600 transition-colors"
                    style={{ height: barHeight(b.resolved), minHeight: b.resolved > 0 ? '2px' : 0 }}
                  />
                  <div
                    className="w-full bg-amber-400 rounded-b-sm group-hover:bg-amber-500 transition-colors"
                    style={{ height: barHeight(b.pending), minHeight: b.pending > 0 ? '2px' : 0 }}
                  />
                </div>
              ))}
            </div>
            <div className="flex gap-[2px] mt-2">
              {buckets.map((b, i) => (
                <div key={b.date} className="flex-1 min-w-[6px] text-center">
                  <span className="text-[9px] text-gray-400 whitespace-nowrap">
                    {i % labelStep === 0 ? formatDay(b.date) : ''}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Day-by-day table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="text-sm font-semibold text-gray-700">Day-by-Day Breakdown</h2>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400">{buckets.length} days</span>
            {buckets.length > 0 && (
              <ExportMenu
                fileName={`ticket-trend-${days}d`}
                getRows={() => buckets}
                columns={[
                  { header: 'Date', value: (b) => formatExportDate(b.date, false) },
                  { header: 'Created', value: (b) => b.created || 0 },
                  { header: 'Resolved', value: (b) => b.resolved || 0 },
                  { header: 'Pending', value: (b) => b.pending || 0 },
                ]}
              />
            )}
          </div>
        </div>

        <div className="overflow-x-auto max-h-96 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-50">
              <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wider text-gray-500">
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3">Resolved</th>
                <th className="px-4 py-3">Pending</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {[...buckets].reverse().map((b) => (
                <tr key={b.date} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5 text-gray-700 font-medium">
                    {new Date(b.date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}
                  </td>
                  <td className="px-4 py-2.5 text-blue-600 font-medium">{b.created}</td>
                  <td className="px-4 py-2.5 text-green-600 font-medium">{b.resolved}</td>
                  <td className="px-4 py-2.5 text-amber-600 font-medium">{b.pending}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TicketTrendAnalysis;